import { Directive, ElementRef, HostListener, OnDestroy, Renderer2, computed, effect, inject, input } from "@angular/core";

import { FeatureHighlightKey, FeatureHighlightParent } from "./feature-highlight.model";
import { FeatureHighlightService } from "./feature-highlight.service";

let naesteBeskrivelseId = 0;

/**
 * Markerer et element som highlight-punkt. Tekst og aktivering kommer fra Umbraco.
 *
 * `<button featureHighlight="highlight_Feedback">` — pulserer, viser "Ny" og dismisses ved klik.
 * `<button featureHighlightParent="top-nav-burger">` — lyser mens et under-punkt er aktivt; dismisses ikke.
 *
 * Kræver `@include feature-highlight.styles;` i komponentens SCSS.
 */
@Directive({
    selector: "[featureHighlight],[featureHighlightParent]"
})
export class FeatureHighlightDirective implements OnDestroy {
    public readonly featureHighlight = input<FeatureHighlightKey | undefined>(undefined);
    public readonly featureHighlightParent = input<FeatureHighlightParent | undefined>(undefined);
    public readonly featureHighlightPulse = input(true);
    public readonly featureHighlightBadge = input<"corner" | "inline">("corner");

    private readonly service = inject(FeatureHighlightService);
    private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    private readonly renderer = inject(Renderer2);
    private readonly beskrivelseId = `feature-highlight-beskrivelse-${++naesteBeskrivelseId}`;
    private badge: HTMLElement | null = null;
    private beskrivelse: HTMLElement | null = null;

    private readonly tekster = computed((): string[] | null => {
        const key = this.featureHighlight();
        if (key) {
            const highlight = this.service.aktivHighlight(key);
            return highlight ? [highlight.tekst] : null;
        }
        const parent = this.featureHighlightParent();
        const tekster = parent ? this.service.aktivForaelderTekster(parent) : [];
        return tekster.length > 0 ? tekster : null;
    });

    constructor() {
        effect(() => this.render(this.tekster(), this.featureHighlightPulse(), this.featureHighlightBadge()));
    }

    @HostListener("click")
    public onClick(): void {
        const key = this.featureHighlight();
        if (key) {
            this.service.dismiss(key);
        }
    }

    public ngOnDestroy(): void {
        this.fjern();
    }

    private render(tekster: string[] | null, pulse: boolean, badge: "corner" | "inline"): void {
        if (!tekster) {
            this.fjern();
            return;
        }

        this.renderer.addClass(this.host, "feature-highlight");
        this.toggleClass("feature-highlight--pulse", pulse);
        this.toggleClass("feature-highlight--parent", !!this.featureHighlightParent());

        if (!this.badge) {
            this.badge = this.renderer.createElement("span");
            this.renderer.setAttribute(this.badge, "aria-hidden", "true");
            this.renderer.appendChild(this.badge, this.renderer.createText("Ny"));
            this.renderer.appendChild(this.host, this.badge);
        }
        this.renderer.setAttribute(this.badge, "class", `feature-highlight__badge feature-highlight__badge--${badge}`);

        if (!this.beskrivelse) {
            this.beskrivelse = this.renderer.createElement("span");
            this.renderer.addClass(this.beskrivelse, "feature-highlight__beskrivelse");
            this.renderer.setAttribute(this.beskrivelse, "id", this.beskrivelseId);
            this.renderer.appendChild(this.host, this.beskrivelse);
            this.renderer.setAttribute(this.host, "aria-describedby", this.beskrivelseId);
        }
        const udfyldte = tekster.filter((tekst) => tekst.length > 0);
        const beskrivelse = udfyldte.length > 0 ? `Ny funktion: ${udfyldte.join(", ")}` : "Ny funktion";
        this.renderer.setProperty(this.beskrivelse, "textContent", beskrivelse);

        if (udfyldte.length > 0) {
            this.renderer.setAttribute(this.host, "title", udfyldte.join("\n"));
        } else {
            this.renderer.removeAttribute(this.host, "title");
        }
    }

    private fjern(): void {
        this.renderer.removeClass(this.host, "feature-highlight");
        this.renderer.removeClass(this.host, "feature-highlight--pulse");
        this.renderer.removeClass(this.host, "feature-highlight--parent");
        if (this.badge) {
            this.renderer.removeChild(this.host, this.badge);
            this.badge = null;
        }
        if (this.beskrivelse) {
            this.renderer.removeChild(this.host, this.beskrivelse);
            this.beskrivelse = null;
            this.renderer.removeAttribute(this.host, "aria-describedby");
            this.renderer.removeAttribute(this.host, "title");
        }
    }

    private toggleClass(className: string, enabled: boolean): void {
        if (enabled) {
            this.renderer.addClass(this.host, className);
        } else {
            this.renderer.removeClass(this.host, className);
        }
    }
}
